import type { CSSProperties, ReactNode } from 'react';
import { CountryFlag } from './CountryFlag';
import { PlayerAvatar } from './PlayerAvatar';

/**
 * Player Identity System V1 — the shared identity row.
 *
 * Renders the approved arrangement from the Lobby reference:
 *   [country flag] [avatar] [full display name] [badge slot]
 *
 * Rules enforced here so no surface re-implements them:
 *  - Bots NEVER show a country flag (no flag slot at all — not a globe).
 *  - Humans show their flag, or the shared neutral globe fallback when no
 *    country is selected (CountryFlag handles invalid/failed codes).
 *  - The avatar is the authoritative avatar_key or the display-name initial
 *    (PlayerAvatar handles validation and broken-image fallback).
 *  - The FULL name renders with controlled ellipsis and the complete name
 *    preserved in an accessible title attribute — an initial is never a
 *    substitute for the name.
 */
export type PlayerIdentitySize = 'compact' | 'standard' | 'card';

const SIZES: Record<PlayerIdentitySize, { flag: number; avatar: number; font: number; gap: number }> = {
  compact:  { flag: 18, avatar: 24, font: 13, gap: 8 },
  standard: { flag: 24, avatar: 32, font: 14, gap: 10 },
  card:     { flag: 26, avatar: 40, font: 15, gap: 10 },
};

export interface PlayerIdentityProps {
  /** Canonical display name (backend display_name / public_name). */
  name: string;
  countryCode?: string | null;
  avatarKey?: string | null;
  /** Bots never render a flag slot. */
  isBot?: boolean;
  size?: PlayerIdentitySize;
  /** Hide the flag slot entirely (surfaces without a flag in their design). */
  showFlag?: boolean;
  /** Hide the avatar slot (name-plus-flag-only rows). */
  showAvatar?: boolean;
  /** Role badge(s) rendered after the name (HOST/YOU/BOT/READY etc.). */
  badge?: ReactNode;
  style?: CSSProperties;
  nameStyle?: CSSProperties;
  avatarInitialStyle?: CSSProperties;
  flagLoading?: 'eager' | 'lazy';
}

export function PlayerIdentity({
  name,
  countryCode,
  avatarKey,
  isBot = false,
  size = 'standard',
  showFlag = true,
  showAvatar = true,
  badge,
  style,
  nameStyle,
  avatarInitialStyle,
  flagLoading = 'lazy',
}: PlayerIdentityProps) {
  const s = SIZES[size];

  return (
    <div
      data-player-identity="true"
      style={{
        display:    'flex',
        alignItems: 'center',
        gap:        s.gap,
        minWidth:   0,
        ...style,
      }}
    >
      {showFlag && !isBot && (
        <CountryFlag countryCode={countryCode} width={s.flag} loading={flagLoading} decorative />
      )}
      {showAvatar && (
        <PlayerAvatar
          avatarKey={avatarKey}
          name={name}
          size={s.avatar}
          loading={flagLoading}
          initialStyle={avatarInitialStyle}
        />
      )}
      <span
        title={name}
        style={{
          fontSize:     s.font,
          fontWeight:   600,
          color:        '#FFFFFF',
          whiteSpace:   'nowrap',
          overflow:     'hidden',
          textOverflow: 'ellipsis',
          minWidth:     0,
          ...nameStyle,
        }}
      >
        {name}
      </span>
      {badge}
    </div>
  );
}
